import { useAPI } from '@providers/useAPI';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { asrProxyKeys } from 'app/queries/asrProxy/asrProxyKeys';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createFetchAsrHealthQueryFn } from '../api/fetchAsrHealth';
import { createFetchAsrTranscriptionQueryFn } from '../api/fetchAsrTranscription';
import type { AsrJobRow, AsrRetrieveTranscriptionResponse } from '../api/types';
import { isAsrConsoleTab } from '../helpers/isAsrConsoleTab';
import { isAsrHealthResponse } from '../helpers/isAsrHealthResponse';
import type { TTab } from '../types/TTab';

type TPlaygroundPreload = {
  response: AsrRetrieveTranscriptionResponse;
  model?: string;
};

type TState = {
  tab: TTab;
  setTab: (tab: TTab) => void;
  jobsSearch: string;
  setJobsSearch: (search: string) => void;
  online: boolean;
  adapters: string[];
  preload: TPlaygroundPreload | null;
  clearPreload: () => void;
  openInPlayground: (job: AsrJobRow) => Promise<void>;
  openTranscriptionInPlayground: (transcriptionId: string, model?: string) => Promise<void>;
  loadingJobId: string | null;
};

const AsrProxyConsoleContext = createContext<TState>({
  tab: 'playground',
  setTab: () => undefined,
  jobsSearch: '',
  setJobsSearch: () => undefined,
  online: false,
  adapters: [],
  preload: null,
  clearPreload: () => undefined,
  openInPlayground: async () => undefined,
  openTranscriptionInPlayground: async () => undefined,
  loadingJobId: null,
});

const HEALTH_POLL_INTERVAL_MS = 5000;
const TRANSCRIPTION_SEARCH_PARAM = 'transcription';
const TAB_SEARCH_PARAM = 'tab';
const JOBS_SEARCH_PARAM = 'search';

type TProps = {
  children: ReactNode;
};

export const AsrProxyConsoleProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<TTab>('playground');
  const [jobsSearch, setJobsSearch] = useState('');
  const [preload, setPreload] = useState<TPlaygroundPreload | null>(null);
  const [loadingJobId, setLoadingJobId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTranscriptionId = searchParams.get(TRANSCRIPTION_SEARCH_PARAM);
  const requestedTab = searchParams.get(TAB_SEARCH_PARAM);
  const requestedJobsSearch = searchParams.get(JOBS_SEARCH_PARAM);

  const healthQuery = useQuery({
    queryKey: asrProxyKeys.health(),
    queryFn: createFetchAsrHealthQueryFn(call),
    refetchInterval: HEALTH_POLL_INTERVAL_MS,
  });
  const health = !healthQuery.isError && isAsrHealthResponse(healthQuery.data) ? healthQuery.data : null;
  const online = health !== null;
  const adapters = health?.adapters ?? [];

  const openTranscriptionInPlayground = useCallback(
    async (transcriptionId: string, model?: string) => {
      setLoadingJobId(transcriptionId);
      try {
        const result = await queryClient.fetchQuery({
          queryKey: asrProxyKeys.transcription(transcriptionId),
          queryFn: createFetchAsrTranscriptionQueryFn(call),
          staleTime: 0,
        });
        if (result?.transcript || result?.execution) {
          setPreload({ response: result, model });
          setTab('playground');
        }
      } catch {
        setPreload(null);
      } finally {
        setLoadingJobId(null);
      }
    },
    [call, queryClient],
  );

  const openInPlayground = (job: AsrJobRow) => openTranscriptionInPlayground(job.id, job.model);

  useEffect(() => {
    if (!requestedTranscriptionId) {
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(TRANSCRIPTION_SEARCH_PARAM);
        return next;
      },
      { replace: true },
    );
    void openTranscriptionInPlayground(requestedTranscriptionId);
  }, [openTranscriptionInPlayground, requestedTranscriptionId, setSearchParams]);

  useEffect(() => {
    if (requestedTab === null && requestedJobsSearch === null) {
      return;
    }
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(TAB_SEARCH_PARAM);
        next.delete(JOBS_SEARCH_PARAM);
        return next;
      },
      { replace: true },
    );
    if (isAsrConsoleTab(requestedTab)) {
      setTab(requestedTab);
    }
    if (requestedJobsSearch !== null) {
      setJobsSearch(requestedJobsSearch);
    }
  }, [requestedJobsSearch, requestedTab, setSearchParams]);

  const clearPreload = () => setPreload(null);

  return (
    <AsrProxyConsoleContext.Provider
      value={{
        tab,
        setTab,
        jobsSearch,
        setJobsSearch,
        online,
        adapters,
        preload,
        clearPreload,
        openInPlayground,
        openTranscriptionInPlayground,
        loadingJobId,
      }}
    >
      {children}
    </AsrProxyConsoleContext.Provider>
  );
};

export const useAsrProxyConsoleContext = () => useContext(AsrProxyConsoleContext);
